import { useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { JsonLd } from '../components/JsonLd';

const FAQ_ITEMS = [
  { q: 'What does 1 credit get me?', a: 'One full analysis: a 3-model consensus score, trait breakdown (attractiveness, trustworthiness, etc.), specific coaching on what to fix, outfit review, and profile suggestions.' },
  { q: 'What if the analysis fails?', a: 'Your credit is automatically refunded. Zero risk.' },
  { q: 'Do credits expire?', a: 'No. Credits never expire. Use them whenever you want.' },
];

function FaqAccordion({ items }: { items: typeof FAQ_ITEMS }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = useCallback((index: number) => {
    setOpenIndex(prev => (prev === index ? null : index));
  }, []);

  return (
    <div className="space-y-3">
      {items.map((item, i) => {
        const isOpen = openIndex === i;
        return (
          <div key={i} className="card overflow-hidden">
            <button
              onClick={() => toggle(i)}
              className="w-full flex items-center justify-between gap-4 p-5 text-left cursor-pointer"
              aria-expanded={isOpen}
            >
              <span className="font-serif text-base text-ink" style={{ fontWeight: 500 }}>{item.q}</span>
              <svg
                className="shrink-0 w-5 h-5 text-ink-faint transition-transform duration-300"
                style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div
              className="transition-all duration-300 ease-in-out"
              style={{
                maxHeight: isOpen ? '500px' : '0px',
                opacity: isOpen ? 1 : 0,
                overflow: 'hidden',
              }}
            >
              <div className="px-5 pb-5 text-sm text-ink-muted leading-relaxed" style={{ fontWeight: 300 }}>
                {item.a}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function Pricing() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  usePageMetadata({
    title: 'Pricing — PhotoFeeler AI',
    description:
      'PhotoFeeler AI pricing plans. Simple credit-based pricing starting at $5. Your first 3 credits are free.',
    canonicalPath: '/pricing',
  });

  const jsonLdData = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'PhotoFeeler AI Pricing',
      description:
        'Simple credit-based pricing for AI-powered photo analysis. Starter, Standard, and Pro plans available.',
      url: 'https://photofeeler.ai/pricing',
      publisher: {
        '@type': 'Organization',
        name: 'PhotoFeeler AI',
        url: 'https://photofeeler.ai',
      },
    }),
    [],
  );

  const openAuth = () => {
    if (isAuthenticated) navigate('/app');
    else navigate('/?auth=1');
  };

  return (
    <div className="relative overflow-hidden pt-24 pb-20 animate-fade-in">
      <JsonLd data={jsonLdData} />
      <div className="page-orb left-[-5rem] top-24 h-72 w-72 bg-rose/15" />
      <div className="page-orb right-[-6rem] top-28 h-80 w-80 bg-info/10" />

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 text-center mb-20">
        <div className="section-kicker">Pricing</div>
        <h1
          className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink mb-6"
          style={{ fontWeight: 600, letterSpacing: '-0.03em', lineHeight: 1.08 }}
        >
          Simple pricing, built for repeat testing.
        </h1>
        <p className="text-base text-ink-muted max-w-xl mx-auto leading-relaxed" style={{ fontWeight: 300 }}>
          Use the free credits to get the first winner. Upgrade when you want deeper compare, rewrites, retests, and profile progress.
        </p>
      </section>

      {/* Pricing cards */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 mb-24">
        <div className="grid sm:grid-cols-3 gap-5 max-w-2xl mx-auto">
          <div className="card p-6 text-center">
            <div className="text-xs font-sans text-ink-faint tracking-wider uppercase mb-3">Starter</div>
            <div className="text-3xl font-sans text-ink" style={{ fontWeight: 600 }}>$5</div>
            <div className="text-xs text-ink-faint mt-1 mb-4">5 credits &middot; $1/each</div>
            <button onClick={openAuth} className="btn-primary text-xs w-full">Get Starter</button>
          </div>
          <div className="card p-6 text-center">
            <div className="text-xs font-sans text-ink-faint tracking-wider uppercase mb-3">Standard</div>
            <div className="text-3xl font-sans text-ink" style={{ fontWeight: 600 }}>$10</div>
            <div className="text-xs text-ink-faint mt-1 mb-4">10 credits &middot; $1/each</div>
            <button onClick={openAuth} className="btn-primary text-xs w-full">Get Standard</button>
          </div>
          <div className="card-rose p-6 text-center relative">
            <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-sans font-medium text-rose bg-rose-faint px-3 py-0.5 rounded-full border border-rose/15">Best value</div>
            <div className="text-xs font-sans text-rose tracking-wider uppercase mb-3 mt-1">Pro</div>
            <div className="text-3xl font-sans text-ink" style={{ fontWeight: 600 }}>$20</div>
            <div className="text-xs text-rose mt-1 mb-4 font-medium">25 credits &middot; $0.80/each</div>
            <button onClick={openAuth} className="btn-rose text-xs w-full">Get Pro</button>
          </div>
        </div>

        <div className="text-center mt-10">
          <button onClick={openAuth} className="btn-rose text-sm">
            Start free &mdash; 3 credits included
          </button>
        </div>
      </section>

      {/* What you get */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 mb-24">
        <div className="marketing-panel-warm p-6 sm:p-8 max-w-2xl mx-auto">
          <h2 className="font-serif text-2xl text-ink mb-6 text-center" style={{ fontWeight: 600 }}>What every credit includes</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              ['3-model consensus score', 'GPT-4o, Gemini, and Claude rate every photo independently.'],
              ['Trait breakdown', 'Attractiveness, trustworthiness, likability (or competence, influence for LinkedIn).'],
              ['Coaching tips', 'Specific, actionable advice on lighting, expression, outfit, and composition.'],
              ['Profile suggestions', 'Bio and prompt rewrites tailored to the photo and context.'],
            ].map(([title, desc]) => (
              <div key={title} className="card p-4">
                <div className="text-sm font-sans font-medium text-ink mb-1">{title}</div>
                <div className="text-xs text-ink-muted leading-relaxed" style={{ fontWeight: 300 }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 mb-24">
        <div className="section-kicker">Common questions</div>
        <h2 className="section-title max-w-2xl mx-auto">Pricing FAQ</h2>
        <FaqAccordion items={FAQ_ITEMS} />
      </section>

      {/* Final CTA */}
      <section className="max-w-2xl mx-auto px-5 sm:px-8 text-center">
        <div className="glass-note">
          <h3 className="font-serif text-2xl text-ink mb-3" style={{ fontWeight: 600 }}>Ready to test your photos?</h3>
          <p className="text-sm text-ink-faint mb-6" style={{ fontWeight: 300 }}>Three AI models. Fifteen seconds. Your first analysis is free.</p>
          <Link to="/" className="btn-rose text-sm inline-block">Try PhotoFeeler AI &rarr;</Link>
        </div>
      </section>
    </div>
  );
}
